import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { API_V1 } from '@/core/api/endpoints';
import { notesService } from '../api/notes.service';
import { TASK_KEYS } from '@/features/tasks/public';
import { useToast } from '@/shared/hooks/useToast';
import { lookupRegistry } from '@/app/error-registry';
import { extractBackendErrorCode, classifyHttpError } from '@/core/api/http-errors';
import type { ConvertToTaskRequest } from '../types';

/**
 * Mutation hook to convert a note to a task.
 *
 * Only the task caches are invalidated: converting leaves the note untouched, so
 * neither the note nor the notes list changed and refetching them would be work
 * for nothing.
 */
export function useConvertNoteToTask(noteId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('notes');

  return useMutation({
    mutationFn: (payload: ConvertToTaskRequest) => notesService.convertToTask(noteId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.all });
      toast.success({ title: t('convertToTask.messages.success') });
    },
    onError: (error) => {
      const errorCode = extractBackendErrorCode(error);
      const errorType = classifyHttpError(error);
      const { titleKey } = lookupRegistry(
        `${API_V1}/notes/${noteId}/convert-to-task`,
        'POST',
        errorType,
        errorCode,
      );
      const resolvedTitle = titleKey.startsWith('notes:')
        ? t(titleKey.replace('notes:', ''))
        : t(titleKey);
      toast.error({ title: resolvedTitle });
    },
  });
}
